import { Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { AuthLoginPayload } from 'api/actions/auth-login/auth-login-payload';
import { AuthLoginResponse } from 'api/actions/auth-login/auth-login-response';
import { useApi } from 'api/api-context';
import Toast from 'components/Toast';
import { FormMessage } from 'components/forms/FormProps';
import LoginForm from 'components/forms/login/LoginForm';
import TwoFactorLoginForm, { TwoFactorLoginFormData } from 'components/forms/login/TwoFactorLoginForm';
import panic from 'errors/panic';
import SimpleLayout from 'layouts/simple/SimpleLayout';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LOGIN_PAGE_PATH, LOGOUT_PAGE_PATH, PASSWORD_CHANGE_REQUIRED_PAGE_PATH, WELCOME_PAGE_PATH } from 'routes/paths';
import {
  INACTIVE_USER_ERROR_CODE,
  INVALID_EMAIL_OR_PASSWORD_ERROR_CODE,
  INVALID_TWO_FACTOR_CHALLENGE_ERROR_CODE,
  INVALID_TWO_FACTOR_CODE_ERROR_CODE,
} from 'utils/constants';

/**
 * The login page.
 *
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const { login, completeTwoFactorLogin } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [passwordReset, { close: hidePasswordReset }] = useDisclosure(searchParams.get('resetPassword') === 'success');
  const [logout, { close: hideLogout }] = useDisclosure(searchParams.get('logout') === 'true');
  const [message, setMessage] = useState<FormMessage | undefined>(undefined);
  const [twoFactorMessage, setTwoFactorMessage] = useState<FormMessage | undefined>(undefined);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<
    Pick<AuthLoginResponse, 'challengeToken' | 'challengeExpiresIn' | 'email'> | undefined
  >(undefined);

  /**
   * Returns the page to navigate to after successful authentication.
   */
  function getReturnUrl() {
    let returnUrl = searchParams.get('returnUrl');

    if (returnUrl?.includes(LOGIN_PAGE_PATH.original) || returnUrl?.includes(LOGOUT_PAGE_PATH.original)) {
      returnUrl = WELCOME_PAGE_PATH.original;
    }

    return returnUrl ?? WELCOME_PAGE_PATH.original;
  }

  /**
   * Builds the redirect target used when login succeeds but the password is already expired.
   */
  function getPasswordChangeRequiredUrl() {
    return `${PASSWORD_CHANGE_REQUIRED_PAGE_PATH.original}?returnUrl=${encodeURIComponent(getReturnUrl())}`;
  }

  /**
   * Handles the login form submission.
   */
  function onSubmit({ email, password, rememberMe }: AuthLoginPayload) {
    setMessage(undefined);
    setTwoFactorMessage(undefined);

    return login({ payload: { email, password, rememberMe } })
      .then((response) => {
        if (response.requiresTwoFactor && response.challengeToken) {
          setTwoFactorChallenge({
            challengeToken: response.challengeToken,
            challengeExpiresIn: response.challengeExpiresIn,
            email: response.email,
          });

          return;
        }

        if (response.passwordChangeRequired) {
          navigate(getPasswordChangeRequiredUrl());
          return;
        }

        navigate(getReturnUrl());
      })
      .catch((error) => {
        if (error.response.data.error.code === INVALID_EMAIL_OR_PASSWORD_ERROR_CODE) {
          setMessage({ type: 'fail', message: 'Emailová adresa alebo heslo je nesprávne.' });
        } else if (error.response.data.error.code === INACTIVE_USER_ERROR_CODE) {
          setMessage({ type: 'fail', message: 'Váš účet bol deaktivovaný.' });
        } else {
          panic(error);
        }
      });
  }

  /**
   * Handles the second login step with a TOTP or recovery code.
   */
  function onSubmitTwoFactor({ code, isRecoveryCode }: TwoFactorLoginFormData) {
    if (!twoFactorChallenge?.challengeToken) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return Promise.resolve();
    }

    setTwoFactorMessage(undefined);

    return completeTwoFactorLogin({
      payload: {
        challengeToken: twoFactorChallenge.challengeToken,
        code,
        isRecoveryCode,
      },
    })
      .then((response) => {
        if (response.passwordChangeRequired) {
          navigate(getPasswordChangeRequiredUrl());
          return;
        }

        navigate(getReturnUrl());
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.error?.code;

        if (errorCode === INVALID_TWO_FACTOR_CODE_ERROR_CODE) {
          setTwoFactorMessage({
            type: 'fail',
            message: isRecoveryCode
              ? 'Zadaný záložný kód je neplatný alebo už bol použitý.'
              : 'Zadaný overovací kód je neplatný.',
          });
        } else if (errorCode === INVALID_TWO_FACTOR_CHALLENGE_ERROR_CODE) {
          setTwoFactorChallenge(undefined);
          setMessage({ type: 'warning', message: 'Platnosť dvojfaktorového overenia vypršala. Prihláste sa znova.' });
        } else if (errorCode === INACTIVE_USER_ERROR_CODE) {
          setTwoFactorChallenge(undefined);
          setMessage({ type: 'fail', message: 'Váš účet bol deaktivovaný.' });
        } else {
          panic(error);
        }
      });
  }

  // Remove the `resetPassword` query parameter from the URL when the password
  // reset is successful.
  useEffect(() => {
    if (passwordReset) {
      setSearchParams(
        (curr) => {
          curr.delete('resetPassword');
          return curr;
        },
        { replace: true }
      );
    }
  }, [passwordReset]);

  // Remove the `logout` query parameter from the URL when the logout is successful.
  useEffect(() => {
    if (logout) {
      setSearchParams(
        (curr) => {
          curr.delete('logout');
          return curr;
        },
        { replace: true }
      );
    }
  }, [logout]);

  return (
    <SimpleLayout contentStyle={{ position: 'relative', marginTop: passwordReset ? '60px' : 0 }}>
      <Stack pos="absolute" top={-90}>
        <Toast
          type="success"
          message="Heslo bolo úspešne zmenené. Teraz sa môžete prihlásiť."
          opened={passwordReset}
          onClose={hidePasswordReset}
        />
        <Toast
          type="warning"
          message="Boli ste odhlásený. Pokračujte opätovným prihlásením."
          opened={logout}
          onClose={hideLogout}
        />
      </Stack>
      {twoFactorChallenge ? (
        <TwoFactorLoginForm
          email={twoFactorChallenge.email}
          challengeExpiresIn={twoFactorChallenge.challengeExpiresIn}
          onSubmit={onSubmitTwoFactor}
          onBack={() => {
            setTwoFactorChallenge(undefined);
            setTwoFactorMessage(undefined);
          }}
          challengeMessage={twoFactorMessage}
        />
      ) : (
        <LoginForm onSubmit={onSubmit} message={message} />
      )}
    </SimpleLayout>
  );
}
